import React, { useState } from "react";
import axios from "axios";
import MarketingLayout, { Seo, Reveal, PageHero } from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please email support@vozelto.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketingLayout>
      <Seo title="Contact — Vozelto" path="/contact"
        description="Get in touch with the Vozelto team — sales questions, support, partnerships or press. We read every message." />
      <PageHero caps="Contact" title={<>Talk to a human.<br /><span className="brand-text">We read every message.</span></>}
        subtitle="Sales questions, support, partnerships, press — or just tell us what you'd want your AI marketing team to do." />
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="grid lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-3">
            {sent ? (
              <div className="card-matte p-10 text-center" data-testid="contact-success">
                <CheckCircle size={44} className="text-emerald-400 mx-auto mb-4" weight="fill" />
                <h2 className="font-head font-bold text-2xl">Message received</h2>
                <p className="mt-2 text-[#9a9a9a]">Thanks, {form.name.split(" ")[0]} — we'll get back to you at {form.email} shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="card-matte p-7 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="label-caps mb-2">Name *</div>
                    <Input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="h-11 bg-white/5 border-white/10" />
                  </div>
                  <div>
                    <div className="label-caps mb-2">Email *</div>
                    <Input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@business.com" className="h-11 bg-white/5 border-white/10" />
                  </div>
                </div>
                <div>
                  <div className="label-caps mb-2">Company (optional)</div>
                  <Input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Your business" className="h-11 bg-white/5 border-white/10" />
                </div>
                <div>
                  <div className="label-caps mb-2">Message *</div>
                  <Textarea data-testid="contact-message" required value={form.message} onChange={set("message")} rows={5}
                    placeholder="How can we help?" className="bg-white/5 border-white/10 resize-none" />
                </div>
                {error && <div data-testid="contact-error" className="text-sm text-red-400">{error}</div>}
                <Button data-testid="contact-submit" type="submit" disabled={sending}
                  className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-7">
                  <PaperPlaneTilt size={16} className="mr-2" weight="fill" /> {sending ? "Sending…" : "Send Message"}
                </Button>
              </form>
            )}
          </Reveal>
          <Reveal className="lg:col-span-2">
            <div className="card-matte p-7">
              <div className="h-11 w-11 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center mb-4">
                <EnvelopeSimple size={22} className="brand-text" weight="duotone" />
              </div>
              <h3 className="font-head font-semibold text-lg">Prefer email?</h3>
              <p className="mt-2 text-sm text-[#9a9a9a] leading-relaxed">Write to us directly and we'll reply within one business day.</p>
              <a href="mailto:info@vozelto.com" data-testid="contact-direct-email" className="mt-3 inline-block text-sm font-semibold brand-text hover:opacity-80">info@vozelto.com</a>
              <div className="mt-6 pt-5 border-t border-white/8 text-sm text-[#9a9a9a] leading-relaxed">
                <span className="text-white font-semibold">Existing customer?</span> Sign in and use the in-app support, or mention your workspace name here for faster routing.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
